'use client';

import Dropdown from '../../../atoms/Dropdown/Dropdown';
import { dropdownOptions, getValidationResponse } from '../utils';
import { useFormContext } from '../FormContext';
import type { DropdownField } from '../types';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  UseFormGetValues,
  FieldValues,
  FieldErrors,
} from 'react-hook-form';

export type DropdownControlProps = {
  field: DropdownField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  errors: FieldErrors<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
};

/**
 * Form control for dropdown/select fields
 * @component
 * @param {DropdownControlProps} props - Component props
 * @returns {JSX.Element} Dropdown control component
 */
const DropdownControl = ({
  field,
  theme,
  direction,
  register,
  setValue,
  watch,
  getValues,
  errors,
  trigger,
}: DropdownControlProps) => {
  const { disabled: formDisabled } = useFormContext();

  const depField = field.disabledDependency;
  const depValue = depField ? watch(depField.name) : undefined;
  const isDisabledByDependency = depField
    ? depField.value !== undefined
      ? depValue === depField.value
      : !!depValue
    : false;

  const isDisabled = field.disabled || isDisabledByDependency || formDisabled;

  return (
    <Dropdown
      key={`form_field_${field.name}`}
      theme={theme}
      name={field.name}
      label={field.label}
      placeholder={field.placeholder}
      options={field?.options ? field.options : dropdownOptions?.[field.name]}
      multiSelect={field.multiSelect}
      selectedOption={!field.multiSelect ? watch(field.name) : undefined}
      selectedOptions={field.multiSelect ? watch(field.name) || [] : undefined}
      handleSelect={(id) => {
        setValue(field.name, id, { shouldValidate: true });
      }}
      handleMultiSelect={
        field.multiSelect
          ? (values) => {
              setValue(field.name, values, { shouldValidate: true });
            }
          : undefined
      }
      onBlur={() => trigger(field.name)}
      register={() =>
        register(field.name, {
          validate: (value) => {
            // Skip validation if dependency is not met
            if (field.dependency) {
              const depValue = getValues(field.dependency.name);
              if (Array.isArray(depValue)) {
                if (!depValue.includes(field.dependency.value)) {
                  return true;
                }
              } else if (depValue !== field.dependency.value) {
                return true;
              }
            }
            if (field.required && (!value || value === '')) {
              return field.validationMessages?.required || 'This field is required';
            }
            return true;
          },
        })
      }
      required={field.required}
      disabled={isDisabled}
      validationResponse={getValidationResponse({ field: field.name, errors })}
      direction={direction}
    />
  );
};

export default DropdownControl;
